export interface StockLot {
  id: string;
  shares: number;
  price: number;
  date: string;
  currency: 'USD' | 'CAD';
  purchasedDate?: string; // YYYY-MM-DD, optional user-supplied purchase date
}

export interface HistoricalDataPoint {
  date: string;
  price: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
}

export interface Stock {
  id: string;
  symbol: string;
  name?: string;
  exchange?: string;
  currency: string; // 'USD', 'CAD', etc.
  lots: StockLot[];
  currentPrice: number | null;
  lastUpdated: string | null;
  userModifiedAt?: string | null;
  historicalData: HistoricalDataPoint[];
  isLoading: boolean;
  watchlist?: boolean;
  error?: string;
  groundingUrls?: string[];
  marketCap?: number | null;
  nextEarningsDate?: string | null;
  instrumentType?: string | null;
  sector?: string | null;
}

export interface PortfolioSummary {
  totalValue: number;
  totalCost: number;
  totalProfit: number;
  totalProfitPercent: number;
}

export interface StockSearchResult {
  symbol: string;
  name: string;
  exchange: string;
}

export type StocksRecord = Record<string, Stock>;

export interface EarningsMetric {
  metric: string;
  value: string;
  yoyChange: string;
}

export interface EarningsData {
  title: string;
  quarterEnded: string;
  reportedDate: string;
  metrics: EarningsMetric[];
  segmentHighlights: string[];
  grossMargins: string[];
  managementCommentary: string[];
  keyRisks: string[];
  bottomLine: string;
}

export interface EarningsAnalysisResponse {
  earnings: EarningsData;
  sources: string[];
  analyzedAt: string;
  cachedAt: string;
  usage?: {
    used: number;
    limit: number;
  };
}

export interface EarningsAnalysisStatusResponse {
  hasAnalysis: boolean;
  earnings: EarningsData | null;
  sources: string[];
  analyzedAt: string | null;
  cachedAt: string | null;
  lastEarningsAt: string | null;
  usage: {
    used: number;
    limit: number;
  };
}

export interface EarningsHistoryEntry {
  analyzedAt: string;
  cachedAt: string;
  title: string;
  quarterEnded: string;
  reportedDate: string;
}

export const EARNINGS_CONFIG = {
  cacheTTLSeconds: 1209600, // 14 days — same window as research, used for cached-access credit marker TTL
  monthlyLimitPerUser: 50,
  usageTTLSeconds: 3888000, // 45 days
} as const;